import axios from "axios";
import { useState, useEffect } from "react";
import env from "../config";
import { SUPER_ADMIN_AGENCY_INFO, isSuperAdminAgency } from "./agencyConfig";

// Function to get URL parameters
const getUrlParameter = (name) => {
  // 1. Try query string first
  const urlParams = new URLSearchParams(window.location.search);
  const searchParam = urlParams.get(name);
  if (searchParam) return searchParam;

  // 2. Try pathname: /agency_id=superYxadminagency_id or /something/agency_id=superYxadminagency_id
  const pathname = window.location.pathname;
  // Match /agency_id=superYxadminagency_id or /.../agency_id=superYxadminagency_id
  const pathMatch = pathname.match(new RegExp(`${name}=([^&/]+)`));
  return pathMatch ? pathMatch[1] : null;
};

// agent-builder has no login of its own - it's opened as a popup from an
// already-authenticated agencyiframe session, which appends its own token
// to the launch URL. Set it as an axios default (every call site here is a
// plain axios.get/post/put with no shared instance to attach a header to
// otherwise) at module-evaluation time, so it's in place before any
// component's own mount effects can fire a request.
const launchToken = getUrlParameter("token");
if (launchToken) {
  axios.defaults.headers.common["x-auth-token"] = `Bearer ${launchToken}`;
}

const SsoHandler = () => {
  const BASE_URL = `${env.API_URL}/v1`;

  const requestedAgencyId = getUrlParameter("agency_id");

  const buildDefaultSsoData = (agencyData = null) => ({
    id: agencyData?.agentBuilderAccount?.id,
    userUUID: agencyData?.agentBuilderUser?._id,
    chat_username: "agent builder",
    user_email: agencyData?.agentBuilderAccount?.email,
    isGHLUser: false,
    address: "Abbey",
    agenct_config: {
      PluginOn: true,
      allow: true,
      plugins: [],
      agent_limit: 250,
    },
    agency_id: agencyData?.agentBuilderAccount?.agency_id,
    agentBuilder: null,
    apiKey: "281a34b9-610de430-ac8d0c56",
    apps: ["AI Social App"],
    assistant_id: agencyData?.agentBuilderAssistantId,
    assistants: [agencyData?.agentBuilderAssistantId],
    business_name: "agent builder",
    city: "Ledbury",
    conversationId: "",
    country: "UK",
    createdAt: "2024-07-16T14:19:42.278Z",
    default_assistant: agencyData?.agentBuilderAssistantId,
    email: agencyData?.agentBuilderAccount?.email,
    first_name: "",
    isAuto: false,
    is_auto: true,
    is_streaming_Api: true,
    is_support: null,
    last_name: "",
    monthly_token_limit: 0,
    monthly_token_usage: 0,
    nango_connected: false,
    onboarding: {
      website_url: "home.cc",
      email: agencyData?.agentBuilderAccount?.email,
      verified: true,
    },
    openAgentBuilder: false,
    phone_number: "000000000000",
    promptlabels_ids: [],
    selected_assistant_id: null,
    state: "Herefordshire",
    time_zone: "",
    type: "free",
    updatedAt: "2025-07-23T17:46:22.182Z",
    zip_code: "HR* 1BP",
    __v: 0,
    _id: "6696817ea6a7f10715b2f9f4",
  });

  const [ssodata, setssodata] = useState(
    isSuperAdminAgency(requestedAgencyId)
      ? SUPER_ADMIN_AGENCY_INFO
      : buildDefaultSsoData(),
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const applySuperAdminAgencyData = () => {
    setssodata(SUPER_ADMIN_AGENCY_INFO);
    setLoading(false);
    setError(null);
  };

  // Function to fetch location data by ID
  const fetchLocationData = async (locationId) => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(
        `${BASE_URL}/accounts/location?id=${locationId}`,
      );

      if (response?.data?.data) {
        return response.data.data;
      } else {
        throw new Error("No data found for this location ID");
      }
    } catch (error) {
      console.error("Error fetching location data:", error);
      setError(error.message || "Failed to fetch location data");
      return null;
    } finally {
      setLoading(false);
    }
  };

  const checkSSO = async (app) => {
    // Get ID from URL parameters - check for agency_id, id, or locationId
    const agencyId = getUrlParameter("agency_id");
    const locationId = getUrlParameter("id") || getUrlParameter("locationId");

    if (isSuperAdminAgency(agencyId)) {
      applySuperAdminAgencyData();
      return;
    }

    if (agencyId !== "superYxadminagency_id") {
      try {
        setLoading(true);
        setError(null);

        const response = await axios.get(
          `${BASE_URL}/agency/agencyconfig?_id=${agencyId}`,
        );

        if (response?.data?.agencyInfo) {
          const nextAgencyInfo = response?.data?.agencyInfo;
          const agencyData = {
            ...buildDefaultSsoData(nextAgencyInfo),
            agency_id: agencyId,
            ...nextAgencyInfo,
            agentBuilder: nextAgencyInfo?.agentBuilderAssistantId || null,
          };
          setssodata(agencyData);
        } else {
          throw new Error("No agency data found for this agency ID");
        }
      } catch (error) {
        setError(error.message || "Failed to fetch agency data");
        setssodata({ ...buildDefaultSsoData(), agency_id: agencyId });
      } finally {
        setLoading(false);
      }
      return;
    }

    if (locationId) {
      const locationData = await fetchLocationData(locationId);

      if (locationData) {
        // Update SSO data with fetched data
        setssodata(locationData);
        console.log("Location data loaded:", locationData);
      } else {
        // Fall back to static data if fetch fails
        console.warn("Failed to fetch location data, using static data");
        setssodata(buildDefaultSsoData());
      }
    } else {
      // No ID in parameters, use static data
      console.log(
        "No agency ID or location ID found in URL parameters, using static data",
      );
      setssodata(buildDefaultSsoData());
    }
  };

  // Auto-fetch data when component mounts
  useEffect(() => {
    checkSSO();
  }, []);

  return {
    SSO: ssodata,
    checkSSO: checkSSO,
    setssodata,
    loading,
    error,
  };
};

export default SsoHandler;
